/**
 * Lightweight client-side analytics abstraction.
 * Fires events to POST /api/analytics/event. Never blocks the UI on failure.
 * Never includes passwords, tokens, or PII beyond an anonymous session id.
 *
 * NOTE: sessionStorage here holds a non-sensitive random session identifier
 * (e.g. `s_p8x2m3lz_km9f`) used purely to correlate anonymous events for the
 * current tab session. No auth tokens, no PII. Static analyzers flag any
 * sessionStorage use as "sensitive-data storage" — this one is intentionally
 * safe.
 */
import { api } from "@/lib/api";
import * as log from "@/lib/logger";

const SESSION_KEY = "tovra_session_id";

function getSessionId() {
  if (typeof window === "undefined") return null;
  let sid = window.sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export async function track(event, props = {}) {
  try {
    await api.post("/analytics/event", {
      event,
      session_id: getSessionId(),
      props: props || {},
    });
  } catch (err) {
    // Silent — analytics must never break UX.
    log.debug("[analytics] failed", event, err?.response?.status);
  }
}
